import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useFirebase } from "./firebaseProvider";
import { useAuth } from "./authProvider";
import {
  initMessaging,
  onForegroundMessage,
  removeFCMToken,
  requestNotificationPermission,
} from "services/notificationService";
import {
  getNotifications,
  markNotificationAsRead as apiMarkAsRead,
  markAllNotificationsAsRead as apiMarkAllAsRead,
  deleteNotification as apiDeleteNotification,
} from "../api/notifications";
import logger from "utils/logger";

import { toast } from "react-hot-toast";

export const NotificationContext = createContext({});

export const NotificationProvider = ({ children }) => {
  const { myApp } = useFirebase();
  const { user } = useAuth();

  const [permission, setPermission] = useState(
    typeof Notification !== "undefined"
      ? Notification.permission
      : "unsupported",
  );
  // Lista de notificações (histórico + foreground)
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const unsubscribeForegroundRef = useRef(null);
  const initializedRef = useRef(false);

  // Inicializar Firebase Messaging uma única vez
  useEffect(() => {
    if (!myApp || initializedRef.current) return;
    initializedRef.current = true;
    initMessaging(myApp);
  }, [myApp]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await getNotifications();
      setNotifications(data || []);
    } catch (error) {
      logger.error("[Notification] Erro ao buscar notificações:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Solicitar permissão e registrar token quando o usuário fizer login
  useEffect(() => {
    if (!user || !myApp) return;

    const currentPermission =
      typeof Notification !== "undefined"
        ? Notification.permission
        : "unsupported";

    setPermission(currentPermission);

    if (currentPermission === "granted") {
      // Usuário já concedeu permissão anteriormente: garantir token registrado
      requestNotificationPermission(myApp).then((token) => {
        if (token) setPermission("granted");
      });
    }

    fetchNotifications();
    // Se "default" ou "denied": não pedir automaticamente.
    // O usuário precisa ativar manualmente nas Configurações.
  }, [user?.uid, myApp, fetchNotifications]);

  // Listener de mensagens em foreground
  useEffect(() => {
    if (permission !== "granted") return;

    const unsub = onForegroundMessage((payload) => {
      logger.info("[Notification] Mensagem foreground:", payload);

      const title = payload.notification?.title || "iSugar";
      const content = payload.notification?.body || "";

      const notification = {
        id: payload.messageId || Date.now().toString(),
        title: title,
        content: content,
        data: payload.data || {},
        createdAt: new Date().toISOString(),
        unread: true,
        type: payload.data?.type || "system",
      };

      // Toast visual caso a janela não esteja exatamente no chat remetente:
      const currentPath = window.location.pathname;
      const currentQuery = window.location.search;
      const isChatScreenAndSameChat =
        notification.type === "CHAT_MESSAGE" &&
        currentPath.includes("chat-conversation") &&
        currentQuery.includes(notification.data?.chatId);

      if (!isChatScreenAndSameChat) {
        toast(
          (t) => (
            <div
              onClick={() => {
                toast.dismiss(t.id);
                if (notification.data?.clickAction) {
                  // Pode usar handle navigate se houvesse, mas window.location funciona como fallback
                  window.location.href = notification.data.clickAction;
                }
              }}
              style={{ cursor: "pointer" }}
            >
              <strong>{title}</strong>
              <p style={{ fontSize: "14px", margin: "4px 0 0 0" }}>{content}</p>
            </div>
          ),
          { duration: 5000, position: "top-right" },
        );
      }

      setNotifications((prev) => [notification, ...prev].slice(0, 100));
    });

    unsubscribeForegroundRef.current = unsub;
    return () => {
      if (unsubscribeForegroundRef.current) {
        unsubscribeForegroundRef.current();
      }
    };
  }, [permission]);

  // Remover token FCM ao fazer logout
  useEffect(() => {
    if (!user) {
      removeFCMToken().catch(() => {});
      setNotifications([]);
    }
  }, [user]);

  const requestPermission = useCallback(async () => {
    if (!myApp) return null;
    const token = await requestNotificationPermission(myApp);
    setPermission(
      typeof Notification !== "undefined"
        ? Notification.permission
        : "unsupported",
    );
    return token;
  }, [myApp]);

  const markAsRead = useCallback(async (id) => {
    try {
      // Otimista: atualiza local primeiro
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, unread: false } : n)),
      );
      await apiMarkAsRead(id);
    } catch (error) {
      logger.error("[Notification] Erro ao marcar como lida:", error);
      // Opcional: reverter se falhar
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
      await apiMarkAllAsRead();
    } catch (error) {
      logger.error("[Notification] Erro ao marcar todas como lidas:", error);
    }
  }, []);

  const clearNotification = useCallback(async (id) => {
    try {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      await apiDeleteNotification(id);
    } catch (error) {
      logger.error("[Notification] Erro ao deletar notificação:", error);
    }
  }, []);

  const clearAllNotifications = useCallback(() => {
    // Implementar se houver endpoint para deletar todas
    setNotifications([]);
  }, []);

  const unreadCount = notifications.filter((n) => n.unread).length;

  return (
    <NotificationContext.Provider
      value={{
        permission,
        notifications,
        unreadCount,
        loading,
        fetchNotifications,
        requestPermission,
        clearNotification,
        clearAllNotifications,
        markAsRead,
        markAllRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
